import AppPageTitle from '@/components/AppPageTitle';
import AppTable from '@/components/AppTable';
import AppTableQuery from '@/components/AppTableQuery';
import usePaginatedQuery from '@/hooks/usePaginatedQuery';
import { getChargeHistory } from '@/services/customer_care.service';
import { columnSorter, formatDate, formatNumber } from '@/utils/formatters';
import { getActiveBadge } from '@/utils/getActiveBadge';
import ErrorStatusWithDetails from '@/components/common/ErrorStatusWithDetails';
import useUniquePackages from '@/hooks/useUniquePackages';
import useMediaQuery, { mediaQueryPoints } from '@/hooks/useMediaQuery';
import dayjs from 'dayjs';
import { useState } from 'react';
import { Space } from 'antd';

// const checkStatus = (code) => {
//   if (code === '0') {
//     return true;
//   }

//   return false;
// };

const START_DATE = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
const END_DATE = dayjs().format('YYYY-MM-DD');

function ChargeHistory() {
  const [time, setTime] = useState([START_DATE, END_DATE]);
  const [searchValue, setSearchValue] = useState(null);
  const [query, setQuery] = useState({
    isdn: null,
    start_date: time[0],
    end_date: time[1],
    pack_id: null,
  });
  const [pageSize, setPageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(1);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const offset = (pageIndex - 1) * pageSize + 1;
  const isMobile = useMediaQuery(`(max-width: ${mediaQueryPoints.md}px)`);

  const columns = [
    {
      title: 'Phone Number',
      dataIndex: 'isdn',
      key: 'isdn',
      fixed: isMobile ? 'left' : '',
      sorter: columnSorter('isdn'),
      render: (text) => (text ? text : '-'),
    },
    {
      title: 'Money',
      dataIndex: 'price',
      key: 'price',
      sorter: columnSorter('price'),
      render: (text) => (text ? formatNumber(text) : '0'),
    },
    {
      title: 'Error Code',
      // dataIndex: 'error_code',
      key: 'error_code',
      render: (record) => {
        const result = record?.status_code?.status === 1;
        const renderStatus = getActiveBadge(result, {
          activeText: 'Success',
          inactiveText: 'Error',
        });
        const message = record?.status_code?.message || 'No details';

        if (!result) {
          return (
            <ErrorStatusWithDetails content={message}>
              <span>{renderStatus}</span>
            </ErrorStatusWithDetails>
          );
        }

        return <span>{renderStatus}</span>;
      },
      sorter: columnSorter('error_code'),
    },
    {
      title: 'Pack ID',
      dataIndex: 'package_code',
      key: 'package_code',
      sorter: columnSorter('package_code'),
      render: (code) => (code ? code : '-'),
    },
    {
      title: 'Reason',
      dataIndex: 'action',
      key: 'action',
      sorter: columnSorter('action'),
      render: (text) => (text ? text : '-'),
    },
    {
      title: 'Channel',
      dataIndex: 'channel',
      key: 'channel',
      sorter: columnSorter('channel'),
      render: (text) => (text ? text : '-'),
    },
    {
      title: 'Transaction ID',
      dataIndex: 'trans_id',
      key: 'trans_id',
      render: (id) => (id ? id : '-'),
      sorter: columnSorter('trans_id'),
    },
    {
      title: 'Charge Time',
      dataIndex: 'response_time',
      key: 'response_time',
      render: (time) => (time ? formatDate(time) : '-'),
      sorter: columnSorter('response_time'),
    },
  ];

  const { data, isLoading, refetch, isRefetching } = usePaginatedQuery(
    () =>
      getChargeHistory({
        ...query,
        limit: pageSize,
        offset: offset,
      }),
    ['charge-history', query, pageSize, offset]
  );

  const handleQuery = () => {
    setQuery({
      isdn: searchValue,
      start_date: time[0],
      end_date: time[1],
      pack_id: selectedFilter,
    });
    setPageIndex(1);
  };

  const handleReset = () => {
    setQuery({
      isdn: null,
      start_date: START_DATE,
      end_date: END_DATE,
      pack_id: null,
    });
    setSearchValue(null);
    setSelectedFilter(null);
    setTime([START_DATE, END_DATE]);
    setPageIndex(1);
  };

  const packages = useUniquePackages();

  const filterOptions = packages?.map((item) => ({ label: item.key, value: item.key }));

  return (
    <Space direction="vertical" className="w-full">
      <AppPageTitle title="Charge History" />
      <AppTableQuery
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        hasDatePicker={true}
        rangePicker
        hasFilter={true}
        filterLabel="Package ID"
        filterPlaceholder="Package ID"
        filterOptions={[{ label: 'All', value: null }, ...filterOptions]}
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
        searchPlaceholder="Phone number"
        time={time}
        setTime={setTime}
        handleQuery={handleQuery}
        handleReset={handleReset}
      />

      <AppTable
        columns={columns}
        dataSource={data?.data}
        totalItems={data?.total}
        loading={isLoading || isRefetching}
        onRefresh={refetch}
        pageIndex={pageIndex}
        pageSize={pageSize}
        onPageChange={(page, size) => {
          setPageIndex(page);
        }}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
        }}
      />
    </Space>
  );
}

export default ChargeHistory;
